import { useState, useEffect } from "react";
import { Switch, Route, Link, useLocation } from "wouter";
import { queryClient, apiRequest, applyRuntimeConfig } from "./lib/queryClient";
import type { RuntimeConfigSettings } from "@shared/v2/runtime-config";
import { QueryClientProvider, useMutation, useQuery } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { useToast } from "@/hooks/use-toast";
import { ErrorDialogProvider, useErrorDialog } from "@/hooks/use-error-dialog";
import { TooltipProvider } from "@/components/ui/tooltip";
import { useAuth } from "@/hooks/use-auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import NotFound from "@/pages/not-found";
import SummaryPage from "@/v2/pages/summary";
import RegisterPage from "@/v2/pages/action-item-register";
import UpdatesPage from "@/v2/pages/updates";
import ActionItemDetailPage from "@/v2/pages/action-item-detail";
import AdminPage from "@/v2/pages/admin";
import SettingsPage from "@/v2/pages/settings";
import OriginTracePage from "@/v2/pages/origin-trace";
import MeetingMinutesPage from "@/v2/pages/meeting-minutes";
import MdMeetingsPage from "@/v2/pages/md-meetings";
import HrDossierPage from "@/v2/pages/hr-dossier";
import EscalationsPage from "@/v2/pages/escalations";
import UserGuidePage from "@/v2/pages/user-guide";
import {
  LayoutDashboard,
  Plus,
  RefreshCw,
  Settings,
  SlidersHorizontal,
  LogOut,
  Shield,
  ClipboardList,
  FileSearch,
  BookOpen,
  Loader2,
  KeyRound,
  ChevronDown,
  Monitor,
  Type,
  Users,
  AlertTriangle,
} from "lucide-react";
import { APP_VERSION, BUILD_DATE } from "@shared/version";
import { useLayout, useLayoutProvider, LayoutContext, type LayoutMode } from "@/hooks/use-layout";
import { ErrorBoundary } from "@/components/ErrorBoundary";

interface BrandingData {
  companyName: string;
  departmentName: string;
  logoBase64: string | null;
  fileUploadsEnabled?: boolean;
}

function useBranding(isPublic: boolean = false) {
  const endpoint = isPublic ? "/api/branding/public" : "/api/branding";
  const query = useQuery<{ data: BrandingData }>({
    queryKey: [endpoint],
    staleTime: 60000,
  });
  return query.data?.data ?? { companyName: "", departmentName: "", logoBase64: null, fileUploadsEnabled: true };
}


function NavLink({ href, children, testId }: { href: string; children: React.ReactNode; testId: string }) {
  const [location] = useLocation();
  const isActive = location === href;

  return (
    <Link
      href={href}
      className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
        isActive
          ? "bg-white/15 text-white"
          : "text-white/70 hover-elevate"
      }`}
      data-testid={testId}
    >
      {children}
    </Link>
  );
}

function ChangePasswordDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (v: boolean) => void }) {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const { toast } = useToast();
  const { showFromApiError } = useErrorDialog();

  const mutation = useMutation({
    mutationFn: async () => {
      await apiRequest("POST", "/api/change-password", { currentPassword, newPassword });
    },
    onSuccess: () => {
      toast({ title: "Password changed successfully." });
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      onOpenChange(false);
    },
    onError: (err: Error) => {
      showFromApiError("Password Change Failed", err);
    },
  });

  const mismatch = confirmPassword.length > 0 && newPassword !== confirmPassword;
  const tooShort = newPassword.length > 0 && newPassword.length < 8;
  const canSubmit = currentPassword.length > 0 && newPassword.length >= 8 && newPassword === confirmPassword && !mutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0d1b3e] border-white/10 max-w-sm">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <KeyRound className="w-4 h-4 text-cyan-400" />
            Change Password
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 pt-2">
          <div className="space-y-1.5">
            <Label className="text-white/60 text-xs">Current Password</Label>
            <Input
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              className="bg-white/5 border-white/10 text-white"
              data-testid="input-current-password"
              autoComplete="current-password"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-white/60 text-xs">New Password</Label>
            <Input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="bg-white/5 border-white/10 text-white"
              data-testid="input-new-password"
              autoComplete="new-password"
            />
            {tooShort && (
              <p className="text-xs text-red-400">Must be at least 8 characters</p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label className="text-white/60 text-xs">Confirm New Password</Label>
            <Input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="bg-white/5 border-white/10 text-white"
              data-testid="input-confirm-password"
              autoComplete="new-password"
            />
            {mismatch && (
              <p className="text-xs text-red-400">Passwords do not match</p>
            )}
          </div>
        </div>
        <DialogFooter className="pt-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onOpenChange(false)}
            className="text-white/60"
            data-testid="button-cancel-change-password"
          >
            Cancel
          </Button>
          <Button
            size="sm"
            disabled={!canSubmit}
            onClick={() => mutation.mutate()}
            className="bg-cyan-600 hover:bg-cyan-700 text-white"
            data-testid="button-submit-change-password"
          >
            {mutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : "Change Password"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AppHeader() {
  const [location] = useLocation();
  const { user, logout, isAdmin, isItAdmin, isHrAdmin, canViewMdMeetings, canConfirmDroppedTheBall, canRegisterTasks } = useAuth();
  const branding = useBranding(false);
  const layout = useLayout();
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const initials = user
    ? `${(user.firstName || "")[0] || ""}${(user.lastName || "")[0] || ""}`.toUpperCase() || "U"
    : "U";

  return (
    <header className="min-h-[3.5rem] flex items-center justify-between px-6 py-2 border-b border-white/10 bg-[#0d1b3e]/80 backdrop-blur-sm sticky top-0 z-50" data-testid="app-header">
      <div className="flex items-center gap-6">
        <div className="flex items-center gap-2" data-testid="text-app-title">
          {branding.logoBase64 ? (
            <img src={branding.logoBase64} alt="" className="h-6 w-auto object-contain" data-testid="img-header-logo" />
          ) : (
            <Shield className="w-5 h-5 text-cyan-400" />
          )}
          <span className="text-base font-semibold text-white tracking-tight">
            {branding.companyName || "Governance Tracker"}
          </span>
          <span className="text-2xs text-white/25 font-mono ml-1 self-end mb-0.5" data-testid="text-app-version">v{APP_VERSION} · {BUILD_DATE}</span>
        </div>
        <nav className="flex items-center gap-1 flex-wrap">
          <NavLink href="/" testId="link-nav-summary">
            <LayoutDashboard className="w-4 h-4" />
            Summary
          </NavLink>
          <NavLink href="/origins" testId="link-nav-origins">
            <FileSearch className="w-4 h-4" />
            Origins
          </NavLink>
          {canRegisterTasks && (
            <Link
              href="/register"
              className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                location === "/register"
                  ? "text-white"
                  : "text-white/90 hover-elevate"
              }`}
              style={{ backgroundColor: "#28C76F" }}
              data-testid="link-nav-register"
            >
              <Plus className="w-4 h-4" />
              Register
            </Link>
          )}
          <NavLink href="/updates" testId="link-nav-updates">
            <RefreshCw className="w-4 h-4" />
            Updates
          </NavLink>
          {canRegisterTasks && (
            <NavLink href="/meeting-minutes" testId="link-nav-meeting-minutes">
              <ClipboardList className="w-4 h-4" />
              Meeting Tasks
            </NavLink>
          )}
          {canViewMdMeetings && (
            <NavLink href="/md-meetings" testId="link-nav-md-meetings">
              <FileSearch className="w-4 h-4" />
              MD Meetings
            </NavLink>
          )}
          {isHrAdmin && (
            <NavLink href="/hr-dossier" testId="link-nav-hr-dossier">
              <Users className="w-4 h-4" />
              HR Dossier
            </NavLink>
          )}
          {canConfirmDroppedTheBall && (
            <NavLink href="/escalations" testId="link-nav-escalations">
              <AlertTriangle className="w-4 h-4" />
              Escalations
            </NavLink>
          )}
        </nav>
      </div>
      <div className="flex items-center gap-3">
        <NavLink href="/guide" testId="link-nav-guide">
          <BookOpen className="w-4 h-4" />
          User Guide
        </NavLink>
        {isAdmin && (
          <NavLink href="/admin" testId="link-nav-admin">
            <Settings className="w-4 h-4" />
            Admin
          </NavLink>
        )}
        {isItAdmin && (
          <NavLink href="/settings" testId="link-nav-settings">
            <SlidersHorizontal className="w-4 h-4" />
            Settings
          </NavLink>
        )}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-white/5 transition-colors" data-testid="button-user-menu">
              <Avatar className="h-7 w-7">
                <AvatarImage src={user?.profileImageUrl || undefined} alt={user?.firstName || "User"} />
                <AvatarFallback className="text-xs bg-white/10 text-white">{initials}</AvatarFallback>
              </Avatar>
              <span className="text-sm text-white/80 hidden sm:inline">
                {user?.firstName} {user?.lastName}
              </span>
              <ChevronDown className="w-3 h-3 text-white/40" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="bg-[#0d1b3e] border-white/10 min-w-[180px]">
            <DropdownMenuItem
              className="text-white/70 focus:text-white focus:bg-white/10 cursor-pointer"
              onClick={() => setChangePasswordOpen(true)}
              data-testid="button-change-password"
            >
              <KeyRound className="w-4 h-4 mr-2" />
              Change Password
            </DropdownMenuItem>
            <DropdownMenuSeparator className="bg-white/10" />
            <div className="px-2 py-1.5">
              <div className="flex items-center gap-2 text-white/50 text-xs mb-1.5">
                <Monitor className="w-3.5 h-3.5" />
                <span>Display Width</span>
              </div>
              <div className="flex gap-1">
                {layout.allModes.map((m) => (
                  <button
                    key={m}
                    onClick={(e) => { e.stopPropagation(); layout.setMode(m); }}
                    className={`text-xs px-2 py-1 rounded transition-colors ${
                      layout.mode === m
                        ? "bg-cyan-500/20 text-cyan-400 border border-cyan-500/30"
                        : "text-white/50 hover:text-white/80 hover:bg-white/5 border border-transparent"
                    }`}
                    data-testid={`button-layout-${m}`}
                  >
                    {layout.labels[m]}
                  </button>
                ))}
              </div>
            </div>
            <div className="px-2 py-1.5">
              <div className="flex items-center gap-2 text-white/50 text-xs mb-1.5">
                <Type className="w-3.5 h-3.5" />
                <span>Font Size</span>
              </div>
              <div className="flex gap-1">
                {layout.allFontSizes.map((f) => (
                  <button
                    key={f}
                    onClick={(e) => { e.stopPropagation(); layout.setFontSize(f); }}
                    className={`text-xs px-3 py-1 rounded transition-colors ${
                      layout.fontSize === f
                        ? "bg-cyan-500/20 text-cyan-400 border border-cyan-500/30"
                        : "text-white/50 hover:text-white/80 hover:bg-white/5 border border-transparent"
                    }`}
                    data-testid={`button-font-${f}`}
                  >
                    {layout.fontLabels[f]}
                  </button>
                ))}
              </div>
            </div>
            <DropdownMenuSeparator className="bg-white/10" />
            <DropdownMenuItem
              className="text-red-400 focus:text-red-300 focus:bg-white/10 cursor-pointer"
              onClick={() => logout()}
              data-testid="button-logout"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Sign Out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <ChangePasswordDialog open={changePasswordOpen} onOpenChange={setChangePasswordOpen} />
    </header>
  );
}

function LandingPage() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const branding = useBranding(true);

  const loginMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch("/api/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ username, password }),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.message || "Login failed");
      }
      return res.json();
    },
    onSuccess: () => {
      // Hard navigation to /updates — wipes any in-memory state from a
      // previous session and lands every user (super admin / admin / member
      // alike) on the personal Updates page as their default home.
      window.location.assign("/updates");
    },
    onError: (err: Error) => {
      setError(err.message);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    loginMutation.mutate();
  };

  const displayName = branding.companyName || "Governance Tracker";

  return (
    <div className="min-h-screen flex" data-testid="landing-page">
      <div className="hidden lg:flex lg:w-1/2 flex-col justify-between p-12 bg-gradient-to-br from-[#0d1b3e] via-[#1a2a52] to-[#0f1d40]">
        <div className="flex items-center gap-3">
          {branding.logoBase64 ? (
            <img src={branding.logoBase64} alt="" className="h-8 w-auto object-contain" data-testid="img-login-logo" />
          ) : (
            <Shield className="w-6 h-6 text-cyan-400" />
          )}
          <div>
            <span className="text-lg font-semibold text-white">{displayName}</span>
            {branding.departmentName && (
              <span className="block text-xs text-white/50">{branding.departmentName}</span>
            )}
          </div>
        </div>
        <div>
          <h1 className="text-4xl font-bold text-white mb-4 leading-tight">
            Board Request &<br />Governance Tracking
          </h1>
          <p className="text-white/60 text-lg max-w-md">
            Track action items, manage authority lanes, enforce workflows, and maintain full audit compliance across your governance operations.
          </p>
        </div>
        <div className="flex items-center gap-6 flex-wrap">
          <div className="flex items-center gap-2">
            <ClipboardList className="w-4 h-4 text-cyan-400" />
            <span className="text-white/50 text-sm">Action Item Lifecycle</span>
          </div>
          <div className="flex items-center gap-2">
            <Shield className="w-4 h-4 text-green-400" />
            <span className="text-white/50 text-sm">Sign-off Enforcement</span>
          </div>
          <div className="flex items-center gap-2">
            <Settings className="w-4 h-4 text-purple-400" />
            <span className="text-white/50 text-sm">Configurable Governance</span>
          </div>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-8 bg-[#0a1628]">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center justify-center gap-3 mb-8">
            {branding.logoBase64 ? (
              <img src={branding.logoBase64} alt="" className="h-8 w-auto object-contain" />
            ) : (
              <Shield className="w-6 h-6 text-cyan-400" />
            )}
            <div className="text-center">
              <span className="text-lg font-semibold text-white">{displayName}</span>
              {branding.departmentName && (
                <span className="block text-xs text-white/50">{branding.departmentName}</span>
              )}
            </div>
          </div>
          <h2 className="text-2xl font-bold text-white mb-2 text-center">Welcome</h2>
          <p className="text-white/50 mb-8 text-center">Sign in to access the governance system</p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="username" className="text-white/70">Username</Label>
              <Input
                id="username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Enter your username"
                className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
                data-testid="input-username"
                autoComplete="username"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-white/70">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
                data-testid="input-password"
                autoComplete="current-password"
              />
            </div>

            {error && (
              <p className="text-red-400 text-sm text-center" data-testid="text-login-error">{error}</p>
            )}

            <Button
              type="submit"
              className="w-full"
              size="lg"
              disabled={loginMutation.isPending || !username || !password}
              data-testid="button-login"
            >
              {loginMutation.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                "Sign In"
              )}
            </Button>
          </form>
          <div className="mt-4 text-center">
            <button
              type="button"
              className="text-cyan-400/70 hover:text-cyan-400 text-sm underline-offset-2 hover:underline transition-colors"
              onClick={() => window.location.href = "/forgot-password"}
              data-testid="link-forgot-password"
            >
              Forgot Password?
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function PermissionRoute({ component: Component, permission }: { component: React.ComponentType<any>; permission: string }) {
  const { hasPermission, isLoading } = useAuth();
  const [, setLocation] = useLocation();
  if (isLoading) return null;
  if (!hasPermission(permission)) {
    setLocation("/");
    return null;
  }
  return <Component />;
}

/**
 * Boot-time runtime config fetcher. Mounted only inside AuthenticatedApp so
 * the request goes out with a session cookie. The query lives in the
 * "configuration" tier (refreshes every ~15min) and on every successful
 * fetch we push the new settings into the QueryClient via applyRuntimeConfig
 * — this is the "change gears" plumbing the IT-admin Settings tab uses.
 *
 * The dedicated invalidation hook on the Settings tab triggers a refetch
 * of this query immediately after a save so the saving admin's own session
 * picks up the new cadence without a page refresh. Other connected users
 * pick it up on their next configuration-tier refresh (Flavour A).
 */
function RuntimeConfigBootstrap() {
  const { data } = useQuery<{ data: { settings: RuntimeConfigSettings } }>({
    queryKey: ["/api/runtime-config"],
  });
  useEffect(() => {
    if (data?.data?.settings) {
      applyRuntimeConfig(data.data.settings);
    }
  }, [data]);
  return null;
}

function AuthenticatedApp() {
  const layoutValue = useLayoutProvider();
  return (
    <LayoutContext.Provider value={layoutValue}>
    <RuntimeConfigBootstrap />
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-[#0d1b3e] via-[#1a2a52] to-[#0f1d40]">
      <AppHeader />
      <main className="flex-1 overflow-auto">
        <Switch>
          <Route path="/" component={SummaryPage} />
          <Route path="/register" component={RegisterPage} />
          <Route path="/updates" component={UpdatesPage} />
          <Route path="/meeting-minutes" component={MeetingMinutesPage} />
          <Route path="/md-meetings" component={MdMeetingsPage} />
          <Route path="/hr-dossier">{() => <PermissionRoute component={HrDossierPage} permission="view_hr_dossier" />}</Route>
          <Route path="/escalations">{() => <PermissionRoute component={EscalationsPage} permission="confirm_dropped_the_ball" />}</Route>
          <Route path="/origins" component={OriginTracePage} />
          <Route path="/action-items/:id" component={ActionItemDetailPage} />
          <Route path="/guide" component={UserGuidePage} />
          <Route path="/guide/*" component={UserGuidePage} />
          <Route path="/admin">{() => <PermissionRoute component={AdminPage} permission="view_admin_panel" />}</Route>
          <Route path="/settings">{() => <PermissionRoute component={SettingsPage} permission="view_settings_panel" />}</Route>
          <Route component={NotFound} />
        </Switch>
      </main>
    </div>
    </LayoutContext.Provider>
  );
}

function ForgotPasswordPage() {
  const [username, setUsername] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [isPending, setIsPending] = useState(false);
  const branding = useBranding(true);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setMessage("");
    setIsPending(true);
    try {
      const res = await fetch("/api/forgot-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username: username.trim() }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.message || "Failed to send reset link");
      } else {
        setMessage(data.message);
      }
    } catch {
      setError("An error occurred. Please try again.");
    } finally {
      setIsPending(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-8 bg-[#0a1628]" data-testid="forgot-password-page">
      <div className="w-full max-w-sm">
        <div className="flex items-center justify-center gap-3 mb-8">
          {branding.logoBase64 ? (
            <img src={branding.logoBase64} alt="" className="h-8 w-auto object-contain" />
          ) : (
            <Shield className="w-6 h-6 text-cyan-400" />
          )}
        </div>
        <h2 className="text-2xl font-bold text-white mb-2 text-center">Reset Password</h2>
        <p className="text-white/50 mb-6 text-center text-sm">Enter your username and we'll send a reset link to your registered email address.</p>

        {message ? (
          <div className="space-y-4">
            <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
              <p className="text-green-400 text-sm text-center" data-testid="text-reset-success">{message}</p>
            </div>
            <Button
              className="w-full"
              variant="ghost"
              onClick={() => window.location.href = "/"}
              data-testid="button-back-to-login"
            >
              Back to Sign In
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="reset-username" className="text-white/70">Username</Label>
              <Input
                id="reset-username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Enter your username"
                className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
                data-testid="input-forgot-username"
                autoComplete="username"
              />
            </div>

            {error && (
              <p className="text-red-400 text-sm text-center" data-testid="text-forgot-error">{error}</p>
            )}

            <Button
              type="submit"
              className="w-full"
              size="lg"
              disabled={isPending || !username.trim()}
              data-testid="button-send-reset"
            >
              {isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send Reset Link"}
            </Button>
            <Button
              type="button"
              className="w-full"
              variant="ghost"
              size="lg"
              onClick={() => window.location.href = "/"}
              data-testid="button-back-to-login-2"
            >
              Back to Sign In
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}

function ResetPasswordPage() {
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [isPending, setIsPending] = useState(false);
  const branding = useBranding(true);

  const params = new URLSearchParams(window.location.search);
  const token = params.get("token");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setMessage("");

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }
    if (newPassword.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }

    setIsPending(true);
    try {
      const res = await fetch("/api/reset-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, newPassword }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.message || "Failed to reset password");
      } else {
        setMessage(data.message);
      }
    } catch {
      setError("An error occurred. Please try again.");
    } finally {
      setIsPending(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center p-8 bg-[#0a1628]">
        <div className="w-full max-w-sm text-center">
          <p className="text-red-400 mb-4">Invalid or missing reset link.</p>
          <Button variant="ghost" onClick={() => window.location.href = "/"} data-testid="button-back-invalid">
            Back to Sign In
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-8 bg-[#0a1628]" data-testid="reset-password-page">
      <div className="w-full max-w-sm">
        <div className="flex items-center justify-center gap-3 mb-8">
          {branding.logoBase64 ? (
            <img src={branding.logoBase64} alt="" className="h-8 w-auto object-contain" />
          ) : (
            <Shield className="w-6 h-6 text-cyan-400" />
          )}
        </div>
        <h2 className="text-2xl font-bold text-white mb-2 text-center">Set New Password</h2>
        <p className="text-white/50 mb-6 text-center text-sm">Choose a new password for your account.</p>

        {message ? (
          <div className="space-y-4">
            <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
              <p className="text-green-400 text-sm text-center" data-testid="text-password-reset-success">{message}</p>
            </div>
            <Button
              className="w-full"
              onClick={() => window.location.href = "/"}
              data-testid="button-go-to-login"
            >
              Sign In
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="new-password" className="text-white/70">New Password</Label>
              <Input
                id="new-password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Min 8 characters"
                className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
                data-testid="input-new-password"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm-new-password" className="text-white/70">Confirm Password</Label>
              <Input
                id="confirm-new-password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Re-enter your new password"
                className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
                data-testid="input-confirm-new-password"
              />
            </div>

            {error && (
              <p className="text-red-400 text-sm text-center" data-testid="text-reset-error">{error}</p>
            )}

            <Button
              type="submit"
              className="w-full"
              size="lg"
              disabled={isPending || !newPassword || !confirmPassword}
              data-testid="button-reset-password"
            >
              {isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : "Reset Password"}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}

function AppContent() {
  const { isLoading, isAuthenticated } = useAuth();
  const [location] = useLocation();
  const branding = useBranding(true);

  useEffect(() => {
    if (branding.logoBase64) {
      const link = document.querySelector("link[rel='icon']") as HTMLLinkElement | null;
      if (link) {
        link.href = branding.logoBase64;
      }
    }
  }, [branding.logoBase64]);

  if (location === "/forgot-password") {
    return <ForgotPasswordPage />;
  }
  if (location === "/reset-password") {
    return <ResetPasswordPage />;
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0d1b3e]">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" />
          <span className="text-white/60 text-sm">Loading...</span>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <LandingPage />;
  }

  return <AuthenticatedApp />;
}

function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <ErrorDialogProvider>
            <AppContent />
            <Toaster />
          </ErrorDialogProvider>
        </TooltipProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
